
import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Start writing...',
  className,
}: RichTextEditorProps) => {
  const [editorValue, setEditorValue] = useState(value);
  const quillRef = useRef<ReactQuill>(null);
  
  // Sync with external value
  useEffect(() => {
    if (value !== editorValue) {
      setEditorValue(value);
    }
  }, [value]);

  const handleChange = (content: string) => {
    setEditorValue(content);
    onChange(content);
  };

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    
    input.onchange = async () => {
      try {
        if (!input.files || !input.files[0]) return;
        
        const file = input.files[0];
        
        // Create a unique file name
        const fileExt = file.name.split('.').pop();
        const fileName = `${uuidv4()}_${Date.now()}.${fileExt}`;
        const filePath = `kb-images/${fileName}`;
        
        // Get the Quill editor instance
        if (!quillRef.current) return;
        
        const editor = quillRef.current.getEditor();
        // Make sure we have a valid selection range
        const range = editor.getSelection() || { index: editor.getLength(), length: 0 };
        
        // Insert temporary text
        editor.insertText(range.index, 'Uploading image...', { italic: true });
        
        // Upload the file to Supabase
        const { data, error } = await supabase.storage
          .from('kb-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
          });
          
        if (error) throw error;
        
        // Get the public URL
        const { data: { publicUrl } } = supabase.storage
          .from('kb-images')
          .getPublicUrl(filePath);
          
        // Remove the placeholder text
        editor.deleteText(range.index, 'Uploading image...'.length);
        // Insert the image at cursor position
        editor.insertEmbed(range.index, 'image', publicUrl);
        editor.setSelection(range.index + 1, 0);
      } catch (error) {
        console.error('Error uploading image:', error);
        if (!quillRef.current) return;
        
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection() || { index: editor.getLength(), length: 0 };
        editor.deleteText(range.index, 'Uploading image...'.length);
        editor.insertText(range.index, 'Failed to upload image.', { bold: true, color: 'red' });
      }
    };
  }, []);

  // Module configuration including image upload handler
  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: imageHandler
      }
    },
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
    'list', 'bullet', 'indent',
    'link', 'image',
    'script',
  ];

  return (
    <div className={cn('rich-text-editor w-full', className)}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorValue}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        className="bg-background border rounded-lg"
      />
    </div>
  );
};
